import { AnimatePresence, Reorder, motion, useDragControls } from "motion/react";
import { useEffect, useRef, useState } from "react";
import type { Player } from "@mdj/shared";
import { api } from "../lib/api";
import { Button, GlassCard } from "../components/Glass";

// Home: the team's batting order. Add everyone first, drag the handle to
// reorder (saved on drop), then set each player's song and announcement.
export function Lineup({ onSetSong, onAnnounce }: { onSetSong: (p: Player) => void; onAnnounce: (p: Player) => void }) {
  const [players, setPlayers] = useState<Player[] | null>(null);
  const [name, setName] = useState(""); const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const nameInput = useRef<HTMLInputElement>(null);

  useEffect(() => { api.listPlayers().then(setPlayers).catch((e: Error) => setErr(e.message)); }, []);
  useEffect(() => { if (!saved) return; const t = setTimeout(() => setSaved(false), 1500); return () => clearTimeout(t); }, [saved]);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true); setErr(null);
    try {
      const p = await api.createPlayer({ name: name.trim(), number: number.trim() || null });
      setPlayers((ps) => [...(ps ?? []), p]); setName(""); setNumber(""); nameInput.current?.focus();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const persistOrder = async (next: Player[]) => {
    setPlayers(next);
    try { await api.reorderPlayers(next.map((p) => p.id)); setSaved(true); }
    catch (e) { setErr(`Couldn't save the order: ${(e as Error).message}`); }
  };

  const edit = async (p: Player, patch: { name: string; number: string | null }) => {
    try { const u = await api.updatePlayer(p.id, patch); setPlayers((ps) => (ps ?? []).map((x) => (x.id === u.id ? u : x))); }
    catch (e) { setErr((e as Error).message); }
  };

  const remove = async (p: Player) => {
    if (!confirm(`Remove ${p.name} from the lineup?`)) return;
    try { await api.deletePlayer(p.id); setPlayers((ps) => (ps ?? []).filter((x) => x.id !== p.id)); }
    catch (e) { setErr((e as Error).message); }
  };

  return (
    <GlassCard key="lineup">
      <div className="row" style={{ alignItems: "baseline" }}>
        <h2 style={{ flex: 1 }}>Lineup</h2>
        <AnimatePresence>{saved && <motion.span key="saved" className="saved" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>Order saved ✓</motion.span>}</AnimatePresence>
      </div>
      <p className="hint">Add the whole team, then drag ≡ to set the batting order. Songs and announcements come after.</p>

      <form className="row add-row" onSubmit={(e) => { e.preventDefault(); void add(); }}>
        <label className="field" style={{ flex: 3 }}><input ref={nameInput} placeholder="Player name" value={name} onChange={(e) => setName(e.target.value)} aria-label="Player name" autoCapitalize="words" /></label>
        <label className="field" style={{ flex: 1, minWidth: 72 }}><input placeholder="#" inputMode="numeric" value={number} onChange={(e) => setNumber(e.target.value)} aria-label="Number" maxLength={3} /></label>
        <Button primary type="submit" disabled={busy || !name.trim()} style={{ flex: "0 0 auto" }}>Add</Button>
      </form>

      {players === null && <p className="hint">Loading lineup…</p>}
      {players?.length === 0 && <p className="hint">No players yet. Add the first one above.</p>}
      {players && players.length > 0 && (
        <Reorder.Group axis="y" values={players} onReorder={setPlayers} className="players lineup" as="ol">
          {players.map((p, i) => (
            <LineupRow key={p.id} player={p} index={i} onDrop={() => persistOrder(players)} onSetSong={onSetSong} onAnnounce={onAnnounce} onRemove={remove} onEdit={edit} />
          ))}
        </Reorder.Group>
      )}
      {err && <p style={{ margin: 0, color: "var(--danger)", fontSize: 13 }}>{err}</p>}
    </GlassCard>
  );
}

function LineupRow({ player: p, index, onDrop, onSetSong, onAnnounce, onRemove, onEdit }: {
  player: Player; index: number; onDrop: () => void; onSetSong: (p: Player) => void; onAnnounce: (p: Player) => void;
  onRemove: (p: Player) => void; onEdit: (p: Player, patch: { name: string; number: string | null }) => Promise<void>;
}) {
  const controls = useDragControls();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(p.name); const [number, setNumber] = useState(p.number ?? "");
  // A drag ends with a click on the row; that must not toggle the actions.
  const dragged = useRef(false);
  return (
    <Reorder.Item value={p} as="li" className="lineup-item" dragListener={false} dragControls={controls}
      onDragStart={() => { dragged.current = true; }}
      onDragEnd={() => { onDrop(); setTimeout(() => { dragged.current = false; }, 300); }}
      whileDrag={{ scale: 1.02, boxShadow: "0 12px 32px rgba(0,0,0,0.45)" }}>
      <div className="player lineup-row" onClick={() => { if (!dragged.current) setOpen((o) => !o); }}>
        <span className="slot">{index + 1}</span>
        <span className="num">{p.number ?? "•"}</span>
        <span className="name">{p.name}</span>
        <span className="meta">{p.walkupClipId ? "🎵" : <em>no song</em>}{p.announcementKey ? " 📣" : ""}</span>
        <button type="button" className="handle" aria-label={`Drag to reorder ${p.name}`} onPointerDown={(e) => { e.stopPropagation(); controls.start(e); }} onClick={(e) => e.stopPropagation()}>≡</button>
      </div>
      <AnimatePresence initial={false}>
        {open && !editing && (
          <motion.div key="actions" className="row lineup-actions" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} style={{ overflow: "hidden" }}>
            <Button primary onClick={() => onSetSong(p)}>🎵 {p.walkupClipId ? "Change song" : "Set song"}</Button>
            <Button onClick={() => onAnnounce(p)}>📣 {p.announcementKey ? "Re-record" : "Announce"}</Button>
            <Button ghost icon aria-label={`Edit ${p.name}`} onClick={() => { setName(p.name); setNumber(p.number ?? ""); setEditing(true); }}>✎</Button>
            <Button ghost icon aria-label={`Remove ${p.name}`} onClick={() => onRemove(p)}>✕</Button>
          </motion.div>
        )}
        {open && editing && (
          <motion.form key="edit" className="row lineup-actions" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} style={{ overflow: "hidden" }}
            onSubmit={async (e) => { e.preventDefault(); if (!name.trim()) return; await onEdit(p, { name: name.trim(), number: number.trim() || null }); setEditing(false); }}>
            <label className="field" style={{ flex: 3 }}><input value={name} onChange={(e) => setName(e.target.value)} aria-label={`Name for ${p.name}`} autoFocus /></label>
            <label className="field" style={{ flex: 1, minWidth: 64 }}><input value={number} inputMode="numeric" maxLength={3} onChange={(e) => setNumber(e.target.value)} aria-label={`Number for ${p.name}`} /></label>
            <Button primary type="submit" style={{ flex: "0 0 auto" }}>Save</Button>
            <Button ghost icon type="button" aria-label="Cancel edit" onClick={() => setEditing(false)}>✕</Button>
          </motion.form>
        )}
      </AnimatePresence>
    </Reorder.Item>
  );
}
